/**
 * De JSON de marketplace para atributos desenháveis — função pura.
 *
 * É o coração do tipo `attributeSet`, e a peça de maior retorno do plano. Hoje
 * existem **cinco componentes** que fazem exatamente isto — Shopee (219 l),
 * B2W (276), TikTok (369), ViaVarejo (301) e Mercado Livre (404) — ~1.570 linhas
 * resolvendo cinco vezes o mesmo problema. A razão é sempre a mesma: cada
 * marketplace dá nome e forma diferentes aos mesmos conceitos.
 *
 * A observação que destrava o caso: **as diferenças são todas declaráveis.**
 * Nenhuma exige lógica nova — só dizer onde cada coisa está. É isso que o
 * `AttributeFieldMap` faz, e é por isso que esta função consegue ler os quatro
 * formatos sem um `if` por marketplace.
 *
 * Os três aninhamentos que existem de verdade:
 *
 *  - **`flat`** — lista simples. TikTok.
 *  - **`grouped`** — lista simples, agrupada por um campo. B2W (`grupId`),
 *    ViaVarejo, Meli.
 *  - **`tree`** — atributo filho pendurado num VALOR do atributo pai. Shopee. É
 *    o caso que exige árvore de verdade: a escolha do pai muda quais filhos
 *    aparecem.
 */
import {
  AttributeFieldMap,
  AttributeNesting
} from '../../model/attribute-schema';

/** Uma opção de um atributo. */
export interface AttributeOption {
  readonly value: string;
  readonly label: string;
}

/** Um atributo pronto para desenhar. */
export interface ResolvedAttribute {
  readonly id: string;
  readonly label: string;
  readonly required: boolean;
  readonly multiple: boolean;
  readonly allowFreeText: boolean;
  readonly options: readonly AttributeOption[];
  readonly units: readonly AttributeOption[];
  /** Nome do grupo, quando o aninhamento é `grouped`. Vazio quando não há. */
  readonly group: string;
  /** Atributos que só aparecem quando um valor do pai é escolhido. */
  readonly children: readonly ResolvedAttribute[];
}

/** Como ler este marketplace. */
export interface AttributeReading {
  readonly fields: AttributeFieldMap;
  readonly nesting?: AttributeNesting;
  /** Caminho até a lista de atributos dentro do JSON. */
  readonly sourcePath?: string;
}

/**
 * Profundidade máxima da árvore de atributos.
 *
 * Shopee aninha atributo dentro de VALOR, e um JSON malformado — ou com ciclo —
 * faria a recursão não parar. Cinco níveis é mais do que qualquer categoria real
 * usa, e trunca de forma perceptível em vez de travar a aba.
 */
export const MAX_ATTRIBUTE_DEPTH = 5;

export function resolveAttributes(
  raw: unknown,
  reading: AttributeReading
): readonly ResolvedAttribute[] {

  // Sem caminho declarado, a PROPRIA raiz e a lista — e o formato do Mercado
  // Livre, que devolve os atributos direto no corpo da resposta.
  const lista = reading.sourcePath === undefined || reading.sourcePath.length === 0
    ? raw
    : readPath(raw, reading.sourcePath);

  const items = Array.isArray(lista) ? lista : [];

  const resolvidos: ResolvedAttribute[] = [];

  for (const item of items) {
    resolvidos.push(convert(item, reading, 0));
  }

  return resolvidos;
}

/** Agrupa pelo campo declarado. Sem agrupamento, devolve um grupo só. */
export function groupAttributes(
  attributes: readonly ResolvedAttribute[]
): readonly { name: string; attributes: readonly ResolvedAttribute[] }[] {

  const porGrupo = new Map<string, ResolvedAttribute[]>();

  for (const atributo of attributes) {
    const lista = porGrupo.get(atributo.group);

    if (lista === undefined) {
      porGrupo.set(atributo.group, [atributo]);
      continue;
    }

    lista.push(atributo);
  }

  const grupos: { name: string; attributes: readonly ResolvedAttribute[] }[] = [];

  for (const [name, atributos] of porGrupo) {
    grupos.push({ name, attributes: atributos });
  }

  return grupos;
}

function convert(
  item: unknown,
  reading: AttributeReading,
  depth: number
): ResolvedAttribute {

  const fields = reading.fields;

  return {
    id: asText(readPath(item, fields.id)),
    label: asText(readPath(item, fields.label)),
    required: asBoolean(readPath(item, fields.required)),
    multiple: asBoolean(readPath(item, fields.multiple)),
    allowFreeText: asBoolean(readPath(item, fields.allowFreeText)),
    options: readOptions(item, reading),
    units: readUnits(item, reading),
    group: asText(readPath(item, fields.group)),
    children: readChildren(item, reading, depth)
  };
}

function readOptions(item: unknown, reading: AttributeReading): readonly AttributeOption[] {
  const fields = reading.fields;
  const lista = readPath(item, fields.options);

  if (!Array.isArray(lista)) {
    return [];
  }

  const opcoes: AttributeOption[] = [];

  for (const opcao of lista) {
    // Sem caminho declarado, a própria opção é o valor: há marketplace que
    // devolve uma lista de textos em vez de uma lista de objetos.
    const valor = fields.optionValue === undefined
      ? opcao
      : readPath(opcao, fields.optionValue);

    const rotulo = fields.optionLabel === undefined
      ? opcao
      : readPath(opcao, fields.optionLabel);

    opcoes.push({ value: asText(valor), label: asText(rotulo) || asText(valor) });
  }

  return opcoes;
}

function readUnits(item: unknown, reading: AttributeReading): readonly AttributeOption[] {
  const lista = readPath(item, reading.fields.units);

  if (!Array.isArray(lista)) {
    return [];
  }

  return lista.map(unidade => ({ value: asText(unidade), label: asText(unidade) }));
}

/**
 * Os atributos filhos — só no aninhamento em árvore.
 *
 * A Shopee pendura o filho no VALOR do pai, não no pai: `child_attribute_list`
 * mora dentro de cada item de `attribute_value_list`. Por isso a busca desce
 * pelas opções, e não pelo atributo.
 */
function readChildren(
  item: unknown,
  reading: AttributeReading,
  depth: number
): readonly ResolvedAttribute[] {

  const caminho = reading.fields.children;

  if (caminho === undefined || depth >= MAX_ATTRIBUTE_DEPTH) {
    return [];
  }

  const diretos = readPath(item, caminho);

  if (Array.isArray(diretos)) {
    return diretos.map(filho => convert(filho, reading, depth + 1));
  }

  // Filho pendurado no valor: percorre as opções procurando a lista.
  const opcoes = readPath(item, reading.fields.options);

  if (!Array.isArray(opcoes)) {
    return [];
  }

  const filhos: ResolvedAttribute[] = [];

  for (const opcao of opcoes) {
    const lista = readPath(opcao, caminho);

    if (Array.isArray(lista)) {
      for (const filho of lista) {
        filhos.push(convert(filho, reading, depth + 1));
      }
    }
  }

  return filhos;
}

function readPath(source: unknown, path: string | undefined): unknown {
  if (path === undefined || path.length === 0) {
    return undefined;
  }

  let current = source;

  for (const segment of path.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

function asText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Objeto vira vazio, e não "[object Object]": um caminho errado é erro de
  // configuração, e um rótulo ilegível na tela inteira é a pior forma de
  // descobrir isso.
  return typeof value === 'object' ? '' : String(value);
}

/**
 * Verdadeiro para o que os marketplaces usam como "sim".
 *
 * Não é só booleano: Shopee manda `mandatory: true`, TikTok manda
 * `is_required: 1`, e há quem mande `"true"`. Tratar só `=== true` faria metade
 * dos atributos obrigatórios passarem por opcionais.
 */
function asBoolean(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }

  if (typeof value === 'string') {
    const texto = value.trim().toLowerCase();
    return texto === 'true' || texto === '1' || texto === 'sim' || texto === 'yes';
  }

  return false;
}
